import { Component, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { previewControlReport } from 'src/app/control/store/control.action';
import { ControlEntry, EcabEntry, ItechEntry, StatusEntry, TypologyEntry } from '../model/history.model';
import { selectHasPolicy } from './../../authentication/store/user.selector';
import { ControlReportInfo, CONTROLS_FOLDER_NAME } from './../../control/model/control.model';
import { selectCurrentBuildingId } from './../store/building.selector';
import { TranslocoService, TranslocoDirective, TranslocoPipe } from '@jsverse/transloco';
import { TertiaryButtonComponent } from '../../shared/buttons/tertiary-button.component';
import { DisplayNamePipe } from '../../shared/string.pipe';
import { AsyncPipe, DatePipe } from '@angular/common';

@Component({
  selector: 'building-timeline-entry',
  templateUrl: 'timeline-entry.component.html',
  styleUrls: ['timeline-entry.component.scss'],
  imports: [TranslocoDirective, TertiaryButtonComponent, DisplayNamePipe, TranslocoPipe, AsyncPipe, DatePipe],
})
export class TimelineEntryComponent {
  @Input() typology: TypologyEntry;
  @Input() status: StatusEntry;
  @Input() itech: ItechEntry;
  @Input() ecab: EcabEntry;
  @Input() date: string;

  canDisplayDefects$: Observable<boolean>;

  private controlEntry$ = new BehaviorSubject<ControlEntry | undefined>(undefined);
  private controlEntry: ControlEntry;

  constructor(private router: Router, private store: Store, private translocoService: TranslocoService) {
    this.canDisplayDefects$ = combineLatest([this.controlEntry$, this.store.select(selectHasPolicy('control.defectButton.get'))]).pipe(
      map(
        ([controlEntry, isReadOnly]) =>
          controlEntry !== undefined && controlEntry.defectCount > 0 && (!isReadOnly || controlEntry.isCompliant)
      )
    );
  }

  get control() {
    return this.controlEntry;
  }

  @Input()
  set control(value: ControlEntry) {
    this.controlEntry = value;
    this.controlEntry$.next(value);
  }

  displayControlDefects(controlId: number): void {
    this.router.navigate([], { queryParams: { compliance: controlId } });
  }

  async previewControlReport() {
    if (this.control.reportFileName) {
      const buildingId = await this.store.select(selectCurrentBuildingId).pipe(take(1)).toPromise();
      if (buildingId) {
        const reportInfo: ControlReportInfo = {
          buildingId,
          relativePath: CONTROLS_FOLDER_NAME,
          fileName: this.control.reportFileName,
        };
        this.store.dispatch(previewControlReport({ source: reportInfo }));
      }
    } else {
      this.store.dispatch(previewControlReport({ source: this.control.id }));
    }
  }

  formatChipLabel(value: string | null): string {
    if (!value) {
      return '';
    }
    const key = `building.history.processingTheme.${value}`;
    const translated = this.translocoService.translate(key);
    return translated !== key ? translated : value.replace(/_/g, ' ');
  }

  getItechParams(itech: ItechEntry) {
    const typeTraduit = this.translocoService.translate('building.technicalInstallation.installationType', {
      type: itech.technicalInstallation?.installationType,
    });

    const statusTraduit = this.translocoService.translate('building.technicalInstallation.installationStatus', { status: itech.status });

    return {
      type: `<strong>${typeTraduit}</strong>`,
      status: statusTraduit,
      user: `${itech.user?.firstName} ${itech.user?.lastName}`,
      commissioned:
        itech.isFirstForThisItech && itech.installationDate
          ? this.translocoService.translate('building.technicalInstallation.commissioned', {
              date: new Date(itech.installationDate).toLocaleDateString(),
            })
          : '',
    };
  }
}
